import MainLayout from "../../components/layouts/MainLayout"
import React, { useEffect, useState } from "react";
import { EyeFilled, EditFilled, DeleteFilled, SearchOutlined, ReloadOutlined, PlusOutlined } from "@ant-design/icons";
import { Table, Input, Select, Button, Tag, Space, message, Popconfirm, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Auth } from "../../utils/Auth";
import { CountryApi } from "../../api/countryApi";

const ListCountriesView = () => {
    const navigate = useNavigate()
    const abortController = new AbortController()

    const [countries, setCountries] = useState([])
    const [loading, setLoading] = useState(false)
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(15)

    const [search, setSearch] = useState('')
    const [statut, setStatut] = useState(undefined)

    const [selectedRowKeys, setSelectedRowKeys] = useState([])
    const [bulkLoading, setBulkLoading] = useState(false)

    const buildFilters = (currentPage, currentPerPage) => {
        const filters = { page: currentPage, per_page: currentPerPage }
        if (search) filters.search = search
        if (statut) filters.statut = statut
        return filters
    }

    const handleFetchData = async (currentPage = page, currentPerPage = perPage) => {
        setLoading(true)
        try {
            const res = await CountryApi.getCountries(buildFilters(currentPage, currentPerPage), abortController.signal)

            if (!res || !res.success) {
                setLoading(false)
                return
            }

            setCountries(res.data.data ?? [])
            setTotal(res.data.total ?? 0)
            setPage(res.data.current_page ?? currentPage)
            setPerPage(res.data.per_page ?? currentPerPage)
            setSelectedRowKeys([])
            setLoading(false)
        } catch (error) {
            console.log(error)
            setLoading(false)
            const messages = await error?.messages ?? error?.message

            if (messages instanceof Array)
                return Swal.fire({
                    title: messages.join('\n') !== 'Unauthenticated' ? "Erreur détectée" : "Session expirée",
                    text: messages.join('\n') !== 'Unauthenticated' ? "Veuillez vous reconncter." : "Votre session a expirée veuillez vous reconnecter",
                    icon: "error",
                }).then(() => {
                    if (messages.join('\n') === 'Unauthenticated') {
                        Auth.removeSessionToken()
                        return navigate(`/login`)
                    }
                });
        }
    }

    const handleSearch = () => handleFetchData(1, perPage)

    const handleReset = () => {
        setSearch('')
        setStatut(undefined)
        setTimeout(() => handleFetchData(1, perPage), 0)
    }

    const handleTableChange = (pagination) => handleFetchData(pagination.current, pagination.pageSize)

    const handleDetail = (country) => navigate(`/pays/${country.id}/details`)
    const handleEdit = (country) => navigate(`/pays/${country.id}/modifier`)
    const handleAdd = () => navigate('/pays/ajouter')

    const handleActivate = async (country) => {
        try {
            const res = await CountryApi.activateCountry(country.id, abortController.signal)
            res.success ? message.success(res.message) : message.error(res.message)
            handleFetchData()
        } catch (error) {
            message.error("Échec de l'activation du pays");
        }
    }

    const handleDeactivate = async (country) => {
        try {
            const res = await CountryApi.deactivateCountry(country.id, abortController.signal)
            res.success ? message.success(res.message) : message.error(res.message)
            handleFetchData()
        } catch (error) {
            message.error("Échec de la désactivation du pays");
        }
    }

    const handleDelete = async (country) => {
        try {
            const res = await CountryApi.deleteCountry(country.id, abortController.signal)
            res.success ? message.success(res.message) : message.error(res.message)
            handleFetchData()
        } catch (error) {
            message.error("Échec de la suppression du pays");
        }
    }

    const handleBulkActivate = async () => {
        setBulkLoading(true)
        try {
            const res = await CountryApi.bulkActivateCountries(selectedRowKeys, abortController.signal)
            res.success ? message.success(res.message) : message.error(res.message)
            handleFetchData()
        } catch (error) {
            message.error("Échec de l'activation groupée");
        } finally {
            setBulkLoading(false)
        }
    }

    const handleBulkDeactivate = async () => {
        setBulkLoading(true)
        try {
            const res = await CountryApi.bulkDeactivateCountries(selectedRowKeys, abortController.signal)
            res.success ? message.success(res.message) : message.error(res.message)
            handleFetchData()
        } catch (error) {
            message.error("Échec de la désactivation groupée");
        } finally {
            setBulkLoading(false)
        }
    }

    useEffect(() => {
        handleFetchData(1, perPage)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const columns = [
        { title: 'Nom', dataIndex: 'name', key: 'name' },
        { title: 'Code ISO', dataIndex: 'iso_code', key: 'iso_code' },
        { title: 'Indicatif', dataIndex: 'phone_code', key: 'phone_code' },
        {
            title: 'Statut',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (value) => value ? <Tag color="green">Actif</Tag> : <Tag color="red">Inactif</Tag>,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, country) => (
                <Space>
                    <EyeFilled className="text-green-600 cursor-pointer hover:text-green-800" onClick={() => handleDetail(country)} />
                    <EditFilled className="text-blue-600 cursor-pointer hover:text-blue-800" onClick={() => handleEdit(country)} />
                    {country.is_active ? (
                        <Tooltip title="Désactiver">
                            <Button size="small" onClick={() => handleDeactivate(country)}>Désactiver</Button>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Activer">
                            <Button size="small" type="primary" onClick={() => handleActivate(country)}>Activer</Button>
                        </Tooltip>
                    )}
                    <Popconfirm title="Supprimer ce pays ?" onConfirm={() => handleDelete(country)} okText="Oui" cancelText="Non">
                        <DeleteFilled className="text-red-600 cursor-pointer hover:text-red-800" />
                    </Popconfirm>
                </Space>
            ),
        },
    ]

    return <MainLayout>
        <div className="container mx-auto p-4">
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-xl font-bold">Gestion des pays</h1>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                    Ajouter un pays
                </Button>
            </div>

            <div className="bg-white p-4 rounded shadow mb-4">
                <Space wrap>
                    <Input
                        placeholder="Rechercher (nom, code ISO)"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: 220 }}
                    />
                    <Select
                        placeholder="Statut"
                        value={statut}
                        onChange={setStatut}
                        allowClear
                        style={{ width: 180 }}
                        options={[
                            { value: 'ACTIVE', label: 'Actif' },
                            { value: 'INACTIVE', label: 'Inactif' },
                        ]}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                        Rechercher
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={handleReset}>
                        Réinitialiser
                    </Button>
                </Space>
            </div>

            {selectedRowKeys.length > 0 && (
                <div className="bg-white p-4 rounded shadow mb-4 flex items-center justify-between">
                    <span>{selectedRowKeys.length} pays sélectionné(s)</span>
                    <Space>
                        <Popconfirm
                            title={`Activer ${selectedRowKeys.length} pays ?`}
                            onConfirm={handleBulkActivate}
                            okText="Oui"
                            cancelText="Non"
                        >
                            <Button type="primary" loading={bulkLoading}>Activer la sélection</Button>
                        </Popconfirm>
                        <Popconfirm
                            title={`Désactiver ${selectedRowKeys.length} pays ?`}
                            onConfirm={handleBulkDeactivate}
                            okText="Oui"
                            cancelText="Non"
                        >
                            <Button loading={bulkLoading}>Désactiver la sélection</Button>
                        </Popconfirm>
                    </Space>
                </div>
            )}

            <Table
                dataSource={countries}
                columns={columns}
                rowKey="id"
                loading={loading}
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys,
                }}
                pagination={{
                    current: page,
                    pageSize: perPage,
                    total: total,
                    showSizeChanger: true,
                }}
                onChange={handleTableChange}
                scroll={{ x: true }}
            />
        </div>
    </MainLayout>
}

export default ListCountriesView
